<script setup>
import { onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { CircleUserRound } from 'lucide-vue-next'
import { useAuthStore } from './stores/auth'
import { useUserStore } from './stores/user'
import BottomTabNavigation from './components/ui/BottomTabNavigation.vue'

const route = useRoute()
const authStore = useAuthStore()
const userStore = useUserStore()

const isAuthenticated = computed(() => {
  return (
    authStore.isAuthenticated ||
    import.meta.env.VITE_DEV_AUTH_BYPASS === 'true' ||
    localStorage.getItem('dev-auth-bypass') === 'true'
  )
})

const topBarTitle = computed(() => {
  const titles = {
    home: 'Home',
    exploring: 'People',
    spark: 'Spark',
    'my-loves': 'Loves',
    more: 'More',
    duonamo: 'DuoNamo',
    personals: 'Personals',
    'my-personals': 'My Personals',
    'create-personal': 'New Personal',
    'edit-personal': 'Edit Personal',
    'personal-detail': 'Personal',
    'user-profile': 'Profile',
    admin: 'Admin',
    'memory-game': 'More',
    'seven-facts': 'More',
    'privacy-policy': 'Privacy',
    'ad-replies': 'My Personals',
    'my-replies': 'My Replies'
  }

  return titles[route.name] || 'RogueMixer'
})

// Routes that need full screen (like spark creation)
const isFullScreenRoute = computed(() => {
  return false
})

onMounted(async () => {
  await authStore.checkAuth()
  if (authStore.user) {
    await userStore.fetchProfile(authStore.user.id)
  }
})
</script>

<template>
  <div class="app-shell">
    <header
      v-if="isAuthenticated && !isFullScreenRoute"
      class="app-top-bar"
    >
      <div class="app-chrome-content flex h-16 items-center justify-between">
        <h1 class="font-headline-md text-headline-md text-primary">
          {{ topBarTitle }}
        </h1>

        <router-link
          to="/profile"
          class="flex h-10 w-10 items-center justify-center rounded-full text-primary transition-all duration-150 hover:bg-primary-fixed active:scale-95"
          aria-label="Open profile"
        >
          <CircleUserRound class="h-5 w-5" aria-hidden="true" />
        </router-link>
      </div>
    </header>

    <!-- Main Content -->
    <main
      :class="[
        isFullScreenRoute ? 'app-main-fullscreen' : 'app-main',
        isAuthenticated && !isFullScreenRoute ? 'app-main-authenticated' : '',
        !isAuthenticated || isFullScreenRoute ? 'app-main-guest' : '',
        isAuthenticated ? 'app-main-with-bottom-navigation' : 'app-main-without-bottom-navigation'
      ]"
    >
      <router-view />
    </main>

    <!-- Bottom Tab Navigation (shown when authenticated and not on special routes) -->
    <BottomTabNavigation :is-authenticated="isAuthenticated" />
  </div>
</template>

<style>
.safe-bottom {
  padding-bottom: env(safe-area-inset-bottom);
}
</style>
